import { useState, useEffect } from 'react';
import { FiSettings, FiX } from 'react-icons/fi';
import useStore from './store/useStore';
import MIDIConnection from './components/MIDIConnection';
import ProfileManager from './components/ProfileManager';
import ConfigurationEditor from './components/ConfigurationEditor';
import LooperVisualization from './components/LooperVisualization';
import AIAssistant from './components/AIAssistant';
import MIDIMonitor from './components/MIDIMonitor';
import LoopFileManager from './components/LoopFileManager';
import HelpPage from './components/HelpPage';
import PrtotypeExplainer from './components/PrtotypeExplainer';
import errorLog from './services/errorLog';
import { APP_VERSION } from './constants/version';
import './App.css';

/**
 * Main Application Component
 */

function App() {
    // Selectors: a whole-store subscription here would re-render the entire
    // app shell on every MIDI monitor message.
    const midiInitialized = useStore(state => state.midiInitialized);
    const initMIDI = useStore(state => state.initMIDI);
    const [showSettings, setShowSettings] = useState(false);
    const [activeTab, setActiveTab] = useState('midi');

    useEffect(() => {

        // Initialize MIDI
        if (!midiInitialized) {
            initMIDI();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run ONCE on mount only

    // Check if Web MIDI API is supported
    const isMIDISupported = 'requestMIDIAccess' in navigator;


    return (
        <div className="app">
            <header className="app-header">
                <div className="container">
                    <div className="header-content">
                        <h1 className="app-title">
                            <span className="title-icon">🎸</span>
                            Looper MIDI Control
                        </h1>
                        <p className="header-subtitle">Ed Sheeran Looper Plus & X</p>
                    </div>

                    <button
                        className={`settings-btn ${showSettings ? 'active' : ''}`}
                        onClick={() => setShowSettings(!showSettings)}
                    >
                        {showSettings ? <FiX /> : <FiSettings />}
                        {showSettings ? 'Close' : 'Settings'}
                    </button>
                </div>
            </header>

            <main className="app-main">
                <div className="container">
                    {!isMIDISupported ? (
                        <div className="browser-warning">
                            <h2>⚠️ Web MIDI Not Supported</h2>
                            <p>Your browser doesn't support the Web MIDI API.</p>
                            <ul>
                                <li>✅ Google Chrome</li>
                                <li>✅ Microsoft Edge</li>
                                <li>✅ Opera</li>
                            </ul>
                        </div>
                    ) : showSettings ? (
                        /* Settings Panel */
                        <div className="settings-panel">
                            <div className="settings-tabs">
                                <button
                                    className={`tab-btn ${activeTab === 'midi' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('midi')}
                                >
                                    🎹 MIDI
                                </button>
                                <button
                                    className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('profile')}
                                >
                                    📋 Profiles
                                </button>
                                <button
                                    className={`tab-btn ${activeTab === 'config' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('config')}
                                >
                                    ⚙️ Config
                                </button>
                                <button
                                    className={`tab-btn ${activeTab === 'loops' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('loops')}
                                >
                                    💾 Loop Files
                                </button>
                                <button
                                    className={`tab-btn ${activeTab === 'monitor' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('monitor')}
                                >
                                    📡 Monitor
                                </button>
                                <button
                                    className={`tab-btn ${activeTab === 'help' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('help')}
                                >
                                    ❓ Help
                                </button>
                            </div>

                            <div className="settings-content">
                                {activeTab === 'midi' && <MIDIConnection />}
                                {activeTab === 'profile' && <ProfileManager />}
                                {activeTab === 'config' && <ConfigurationEditor />}
                                {activeTab === 'loops' && <LoopFileManager />}
                                {activeTab === 'monitor' && <MIDIMonitor />}
                                {activeTab === 'help' && <HelpPage />}
                            </div>
                        </div>
                    ) : (
                        /* Main View */
                        <>
                            <LooperVisualization />


                        </>
                    )}
                </div>
            </main>

            <AIAssistant />
            <PrtotypeExplainer />

            <footer className="app-footer">
                <div className="container">
                    <p>© Looper 2026 AI Boutique · v{APP_VERSION}</p>
                    <button
                        className="export-log-btn"
                        onClick={() => errorLog.export()}
                        title="Download the collected error log as JSON"
                    >
                        Export error log
                    </button>
                </div>
            </footer>
        </div>
    );
}

export default App;
